import { useState } from "react";
import axios from "axios";
import { Bell, BellOff, Check, Loader2, Lock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

function ReminderOptIn({ vehicle, user, isPremium }) {
  const [enabled, setEnabled] = useState(false);
  const [weeks, setWeeks] = useState("4");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const canUseReminders = isPremium === 'premium' || isPremium === 'enterprise';

  const handleAddReminder = async () => {
    if (!canUseReminders) {
      toast.error("Reminders are a Premium feature. Please upgrade to use this.");
      return;
    }

    if (!enabled) {
      toast.error("Please tick the checkbox to enable reminders");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/reminders/add`,
        {
          registration: vehicle.registration,
          vehicle_make: vehicle.make,
          vehicle_model: vehicle.model || "Unknown",
          vehicle_colour: vehicle.colour,
          tax_expiry_date: vehicle.taxDueDate,
          mot_expiry_date: vehicle.motExpiryDate,
          reminder_weeks: parseInt(weeks)
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setSuccess(true);
        toast.success(`Reminder set! You'll be emailed ${weeks} weeks before expiry.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set reminder");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="mt-4 border-green-200 bg-green-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-full">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-800">Reminder Set!</p>
              <p className="text-sm text-green-600">
                We&apos;ll email you {weeks} weeks before your Tax/MOT expires.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!canUseReminders) {
    return (
      <Card className="mt-4 border-gray-200 bg-gray-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <div className="bg-gray-200 p-2 rounded-full">
              <Lock className="h-5 w-5 text-gray-500" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-700">Tax/MOT Expiry Reminders</p>
              <p className="text-sm text-gray-500">
                Get email alerts before your vehicle&apos;s Tax or MOT expires.
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = '/#pricing'}
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              Upgrade to Premium
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if there are dates to remind about
  const hasTaxDate = vehicle.taxDueDate && vehicle.taxDueDate !== 'N/A';
  const hasMotDate = vehicle.motExpiryDate && vehicle.motExpiryDate !== 'N/A';

  if (!hasTaxDate && !hasMotDate) {
    return null; // No dates to remind about
  }

  return (
    <Card className="mt-4 border-blue-200 bg-blue-50">
      <CardContent className="py-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Checkbox 
              id="reminder-opt-in"
              checked={enabled}
              onCheckedChange={setEnabled}
              className="mt-1"
            />
            <div>
              <label htmlFor="reminder-opt-in" className="font-medium text-gray-800 cursor-pointer">
                <Bell className="h-4 w-4 inline mr-1" />
                Remind me before Tax/MOT expires
              </label>
              <p className="text-sm text-gray-600 mt-1">
                We&apos;ll send you an email reminder before your vehicle&apos;s Tax or MOT is due.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:ml-auto">
            <Select value={weeks} onValueChange={setWeeks} disabled={!enabled}>
              <SelectTrigger className="w-[140px] bg-white">
                <SelectValue placeholder="Select timing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 weeks before</SelectItem>
                <SelectItem value="8">8 weeks before</SelectItem>
                <SelectItem value="6">6 weeks before</SelectItem>
                <SelectItem value="4">4 weeks before</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              onClick={handleAddReminder}
              disabled={!enabled || loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Set Reminder"
              )}
            </Button>
          </div>
        </div>
        
        {enabled && (
          <div className="mt-3 pt-3 border-t border-blue-200 text-xs text-gray-500">
            <p>
              By setting a reminder, you agree to receive email notifications about this vehicle.
              You can unsubscribe at any time from the email or your dashboard.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReminderOptIn;
